import { animate, style, transition, trigger } from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';

import { takeUntil } from 'rxjs/operators';

import { AnimationUtil, mixinDestroyNotifier } from '@mtna/lib-ui';
import { Catalog, DataProduct, trackByCoreResource } from '@mtna/pojo-consumer-ui';

import { COMPLEX_STANDARD } from '../core/animations';

/**
 * UI to select a data product from a given root catalog.
 * Shows available catalogs and data products within each catalog.
 *
 * @author Will Davis <will.davis@mtna.us
 */
@Component({
  selector: 'rds-data-product-selection',
  templateUrl: './data-product-selection.component.html',
  styleUrls: ['./data-product-selection.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('enterCatalog', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(100%)' }),
        animate(COMPLEX_STANDARD, style({ opacity: 1, transform: 'translateX(0%)' })),
      ]),
    ]),
    AnimationUtil.EXPAND_IN_OUT,
  ],
})
export class RdsDataProductSelectionComponent extends mixinDestroyNotifier() implements AfterViewInit {
  @ViewChildren('fieldContainer') children = new QueryList<ElementRef>();
  hasMoreView: { [key: string]: boolean } = {};
  hideFieldContainer = true;
  showMore: { [key: string]: boolean } = {};

  /** Root catalog of the RDS environment */
  @Input() rootCatalog: Catalog;
  /** The selected catalog */
  @Input()
  set selectedCatalog(selectedCatalog: Catalog) {
    this._selectedCatalog = selectedCatalog;
  }
  get selectedCatalog(): Catalog {
    return this._selectedCatalog;
  }
  private _selectedCatalog: Catalog;
  /** The selected data product */
  @Input() selectedDataProduct: DataProduct | null;

  /** Emitted when a catalog is selected */
  @Output() selectedCatalogChange = new EventEmitter<Catalog>();
  /** Emitted when a data product is selected */
  @Output() selectedDataProductChange = new EventEmitter<DataProduct>();

  /** Track by function for the ngFor loop */
  trackByCr = trackByCoreResource;

  constructor(private cdr: ChangeDetectorRef) {
    super();
  }

  handleCatalogSelection(cat: Catalog) {
    if (cat !== this.selectedCatalog) {
      this.selectedCatalog = cat;
      this.selectedCatalogChange.emit(this.selectedCatalog);
      // Reset the selected data product
      this.handleDataProductSelection(null);
    }
  }

  handleDataProductSelection(dpUri: string | null) {
    if (!dpUri && !!this.selectedDataProduct) {
      // If the data product is being unselected, and it's not already unselected
      // Set the data product back to null/unselected
      this.selectedDataProduct = null;
      this.selectedDataProductChange.emit(this.selectedDataProduct);
    } else if (!!dpUri && (!this.selectedDataProduct || this.selectedDataProduct.uri !== dpUri)) {
      // If the data product uri isn't null and it's not already selected
      // Find the DataProduct in the catalog, and set it as selected
      const dataProduct = this.selectedCatalog.dataProducts.find((dp) => dp.uri === dpUri);
      this.selectedDataProduct = dataProduct;
      this.selectedDataProductChange.emit(this.selectedDataProduct);
    }
  }

  ngAfterViewInit() {
    if (this.children.length) {
      this._registerLargeFieldContainers(this.children);
      this.hideFieldContainer = false;
      this.cdr.markForCheck();
    }
    // Determines whether a catalog details container is larger than 80px.
    this.children.changes.pipe(takeUntil(this.destroyed$)).subscribe((changes: QueryList<ElementRef>) => {
      this.hideFieldContainer = true;
      this.cdr.markForCheck();
      setTimeout(() => {
        this._registerLargeFieldContainers(changes);
        this.hideFieldContainer = false;
        this.cdr.markForCheck();
      }, 1);
    });
  }

  toggleShowMore(key: string) {
    this.showMore[key] = !this.showMore[key];
  }

  private _registerLargeFieldContainers(elements: QueryList<ElementRef>) {
    elements
      .filter((e) => e.nativeElement.getBoundingClientRect().height > 80)
      .forEach((e) => {
        this.hasMoreView[e.nativeElement.id] = true;
      });
  }
}
